import { Building2 } from 'lucide-react';
import { useState, type ReactElement, type ReactNode } from 'react';

import { BackLink } from '@/components/layout/BackLink';
import { TO_PANEL } from '@/lib/back-nav';

import { PermissionErrorPanel } from '@/components/auth/PermissionErrorPanel';
import { GatedChrome, RestrictedPanel } from '@/components/auth/GatedChrome';
import { SegTabs } from '@/components/ui/SegTabs';
import { segPanelId, segTabId } from '@/components/ui/seg-tabs-ids';
import { StatePanel } from '@/components/ui/StatePanel';
import { usePermission, usePermissionState } from '@/hooks/usePermission';
import { useActiveCompany } from '@/lib/active-company-context';

import { AnalysisTab } from './components/AnalysisTab';
import { StandardsTab } from './components/StandardsTab';
import type { Tab } from './types';
import { useTimeStudyUrlState } from './use-url-state';
import { businessToday } from '@/lib/time';
import { skeletonLine } from '@/components/ui/skeleton';

// Pantalla Estudio de tiempos (so=420, spec §4.2): análisis de tiempos contra el
// estándar y gestión de estándares, cada pestaña y tarjeta según su permiso.

export const PERMS = {
  productivity: 'quality:productivity:read',
  rework: 'quality:rework:read',
  standards: 'quality:time-standards:read',
  manageStandards: 'quality:time-standards:manage',
  flow: 'quality:document:transition',
  // G2-4: el responsable asignado también mide su trabajo y ve sus etapas en curso.
  work: 'quality:document:work',
  catalogs: 'quality:catalogs:read',
} as const;

const BREADCRUMB = [
  { label: 'QMS', to: '/dashboard', strong: true },
  { label: 'Estudio de tiempos', strong: true },
];
const TAB_PREFIX = 'ts';

function Frame({ children }: { children: ReactNode }): ReactElement {
  // G4: sin un permiso de la pantalla confirmado, las migas y el encabezado no la nombran.
  const productivity = usePermission(PERMS.productivity);
  const rework = usePermission(PERMS.rework);
  const standards = usePermission(PERMS.standards);
  const state = productivity || rework || standards ? 'granted' : 'denied';
  return (
    <>
      <GatedChrome state={state} breadcrumb={BREADCRUMB} navContext="Estudio de tiempos" />
      <BackLink target={TO_PANEL} />
      {children}
    </>
  );
}

export function TimeStudyScreen(): ReactElement {
  const perm = usePermissionState(PERMS.productivity);
  const canFlow = usePermission(PERMS.flow);
  const canWork = usePermission(PERMS.work);
  const can = {
    deviation: usePermission(PERMS.productivity),
    rework: usePermission(PERMS.rework),
    standards: usePermission(PERMS.standards),
    manage: usePermission(PERMS.manageStandards),
    myStages: canFlow || canWork,
    catalogs: usePermission(PERMS.catalogs),
  };
  const { activeCompanyId, isLoading: companyLoading } = useActiveCompany();
  const [today] = useState(() => businessToday(new Date()));
  const { filters, setFilters, hasFilters, clearFilters } = useTimeStudyUrlState(today);

  const tabs = [
    ...(can.deviation || can.rework ? [{ value: 'analisis' as const, label: 'Análisis' }] : []),
    ...(can.standards ? [{ value: 'estandares' as const, label: 'Estándares' }] : []),
  ];
  const tab: Tab = tabs.some((t) => t.value === filters.tab)
    ? filters.tab
    : (tabs[0]?.value ?? 'analisis');

  if (perm.state === 'error') {
    return (
      <Frame>
        <PermissionErrorPanel error={perm.error} onRetry={perm.retry} retrying={perm.retrying} />
      </Frame>
    );
  }
  if (perm.state === 'loading') {
    return (
      <Frame>
        <div className="card rise" aria-busy="true" style={{ marginTop: 16, padding: 24 }}>
          <span className="sr-only">Cargando</span>
          <span className={skeletonLine({ width: 40, height: 'md' })} aria-hidden="true" />
        </div>
      </Frame>
    );
  }
  if (tabs.length === 0) {
    return (
      <Frame>
        <RestrictedPanel />
      </Frame>
    );
  }
  if (!companyLoading && !activeCompanyId) {
    return (
      <Frame>
        <StatePanel icon={<Building2 />} title="Selecciona una empresa activa" role="status">
          Elige una empresa activa en la barra superior.
        </StatePanel>
      </Frame>
    );
  }

  return (
    <Frame>
      <div className="pagehead rise">
        <div className="grow">
          <h1 style={{ marginBottom: 4 }}>Estudio de tiempos</h1>
          <p style={{ maxWidth: '70ch' }}>
            Tiempo real de cada etapa frente al estándar de producción vigente, reprocesos
            atribuidos y documentos por período.
          </p>
        </div>
      </div>
      <SegTabs
        tabs={tabs}
        value={tab}
        onChange={(t) => setFilters({ tab: t })}
        label="Secciones de Estudio de tiempos"
        idPrefix={TAB_PREFIX}
      />
      {activeCompanyId !== null && (
        <div
          role="tabpanel"
          id={segPanelId(TAB_PREFIX, tab)}
          aria-labelledby={segTabId(TAB_PREFIX, tab)}
          className="tabpanel"
        >
          {tab === 'analisis' ? (
            <AnalysisTab
              filters={filters}
              companyId={activeCompanyId}
              can={can}
              onChange={setFilters}
              onClear={hasFilters ? clearFilters : undefined}
            />
          ) : (
            <StandardsTab companyId={activeCompanyId} canManage={can.manage} />
          )}
        </div>
      )}
    </Frame>
  );
}
